import React, { useEffect } from 'react';

import { LoadingType, PreQualificationStep, setLoadingState, setPreQualificationStep } from 'actions';
import { AlreadyHaveActiveApplicationType } from 'enums/AlreadyHaveActiveApplicationType';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import useQueryParam, { useQueryParams } from 'hooks/useQueryParam';
import NotFoundContact from 'pages/NotFound/NotFoundContact';
import { setPrequalificationPortal } from 'actions/prequalificationPortal';
import { preQualificationApi } from 'api/PreQualification/PreQualificationApi';
import { AppRoutes } from 'routes/routes.enums';
import { setData } from 'actions/data';
import useAppNavigate from 'hooks/useAppNavigate';
import { ContractorNameSource } from 'enums/ApplicationsVariables';
import { getBorrowerByToken } from 'thunks';
import { setBorrower } from 'actions/borrower';

import AlreadyHaveActiveApplication from './steps/AlreadyHaveActiveApplication';
import BorrowerIncome from './steps/BorrowerIncome';
import BorrowerInformation from './steps/BorrowerInformation';
import BorrowerPhoneVerification from './steps/BorrowerPhoneVerification';
import CoBorrowerIncome from './steps/CoBorrowerIncome';
import CoBorrowerInformation from './steps/CoBorrowerInformation';
import CoBorrowerPhoneVerification from './steps/CoBorrowerPhoneVerification';
import ContractorInfo from './steps/ContractorInfo';
import DeclinePage from './steps/DeclinePage/DeclinePage';
import LoanInformation from './steps/LoanInformation';
import NoOptions from './steps/NoOptions';
import NotAbleToAccept from './steps/NotAbleToAccept';
import RedirectToProveLink from './steps/RedirectToProveLink';
import UnfreezeCredit from './steps/UnfreezeCredit';
import BorrowerIdentity from './steps/BorrowerIdentity';

const getPageByStep = (step: PreQualificationStep | null, vfp?: string | null) => {
  switch (step) {
    case PreQualificationStep.BorrowerIdentity:
      return <BorrowerIdentity />;
    case PreQualificationStep.LoanInformation:
      return <LoanInformation />;

    case PreQualificationStep.BorrowerPhoneVerification:
      return <BorrowerPhoneVerification />;

    case PreQualificationStep.CoBorrowerPhoneVerification:
      return <CoBorrowerPhoneVerification />;

    case PreQualificationStep.BorrowerInfo:
      return <BorrowerInformation />;

    case PreQualificationStep.CoBorrowerInfo:
      return <CoBorrowerInformation />;

    case PreQualificationStep.BorrowerIncome:
      return <BorrowerIncome />;

    case PreQualificationStep.CoBorrowerIncome:
      return <CoBorrowerIncome />;

    case PreQualificationStep.Contractor:
      return <ContractorInfo />;

    case PreQualificationStep.AlreadyHaveActiveApplication:
      return <AlreadyHaveActiveApplication pageType={AlreadyHaveActiveApplicationType.Borrower} />;

    case PreQualificationStep.BorrowerAsCoBorrowerAlreadyHaveActiveApplication:
      return <AlreadyHaveActiveApplication pageType={AlreadyHaveActiveApplicationType.BorrowerAsCoBorrower} />;

    case PreQualificationStep.CoBorrowerAlreadyHaveActiveApplication:
      return <AlreadyHaveActiveApplication pageType={AlreadyHaveActiveApplicationType.CoBorrower} />;

    case PreQualificationStep.NotAbleToAccept:
      return <NotAbleToAccept />;

    case PreQualificationStep.UnfreezeCredit:
      return <UnfreezeCredit />;

    case PreQualificationStep.NoOptions:
      return <NoOptions />;

    case PreQualificationStep.Decline:
      return <DeclinePage />;

    case PreQualificationStep.RedirectToProveLink:
      return <RedirectToProveLink vfp={vfp!} />;

    case PreQualificationStep.NotFoundContact:
      return <NotFoundContact />;

    default:
      return <BorrowerIdentity />;
  }
};

const PreQualification = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();

  const currentPreQualificationStep = useAppSelector((state) => state.stages.currentPreQualificationStep);
  const borrowerState = useAppSelector((state) => state.borrower);
  const params = useQueryParams();
  const isFinishAuth = params.has('vfp');
  const vfp = useQueryParam('vfp');

  if (isFinishAuth) {
    dispatch(setPreQualificationStep(PreQualificationStep.RedirectToProveLink));
  }

  const processLoggedInBorrowerApplications = async () => {
    try {
      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: true }));

      const borrower = await dispatch(getBorrowerByToken()).unwrap();

      dispatch(
        setBorrower({
          isLocked: borrower?.locked,
          borrowerFraud: borrower?.variables.borrower_fraud,
        }),
      );

      const { hasBorrowerApplications, hasCoborrowerApplications } =
        await preQualificationApi.checkActiveApplications();

      if (!!borrower?.locked && !hasBorrowerApplications && !hasCoborrowerApplications) {
        navigator(AppRoutes.Prequalification);
        dispatch(setPreQualificationStep(PreQualificationStep.NotAbleToAccept));

        return;
      }

      if (hasBorrowerApplications) {
        navigator(AppRoutes.Prequalification);
        dispatch(setPreQualificationStep(PreQualificationStep.AlreadyHaveActiveApplication));

        return;
      }

      if (hasCoborrowerApplications) {
        navigator(AppRoutes.Prequalification);
        dispatch(setPreQualificationStep(PreQualificationStep.BorrowerAsCoBorrowerAlreadyHaveActiveApplication));

        return;
      }
    } catch (error) {
      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: false }));

      localStorage.clear();
    }
  };

  const handleContractorInfo = async (): Promise<void> => {
    const intermediaryId = params.get('intermediary_id');

    // The promo link params are only on the url the consumer first arrives with. A visit without them is the
    // same application coming back (Prove returns with vfp only, internal navigation drops the query string),
    // so nothing is written here - the contractor stays until the application is submitted.
    if (!intermediaryId) {
      return;
    }

    // The promo link is identified by the intermediary it points at, that is what the application is
    // attributed to. The contractor name is only presentation, it must never decide whether the link counts
    const verifiedIntermediary = await preQualificationApi.getIntermediaryVerificationStatus({ id: intermediaryId });

    const isPromoLinkUsed = !!verifiedIntermediary?.isVerified;
    // The intermediary record is the only source of truth for the name, the url is never trusted for it
    const contractorName = isPromoLinkUsed ? verifiedIntermediary?.intermediaryName || '' : '';

    dispatch(setPrequalificationPortal({ isContractorNameInputDisabled: !!contractorName }));
    dispatch(
      setData(
        isPromoLinkUsed
          ? {
              contractorName: contractorName,
              contractorAutofillUsed: ContractorNameSource.PromoLink,
              promoCode: params.get('promo_code') || '',
              contractorId: intermediaryId || '',
            }
          : {
              // The link points at an intermediary we cannot verify, so it does not count. Anything left from
              // an earlier promo link visit is dropped rather than credited to this application.
              contractorName: '',
              contractorId: '',
              salesPerson: '',
              salespersonData: {},
              promoCode: '',
              contractorAutofillUsed: ContractorNameSource.Empty,
            },
      ),
    );
  };

  const initializePrequalificationFlow = async () => {
    try {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));
      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: false }));

      const isLoggedIn = !!localStorage.getItem('token');

      if (isLoggedIn && borrowerState.borrowerId) {
        await processLoggedInBorrowerApplications();
      }

      await handleContractorInfo();
    } finally {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  };

  useEffect(() => {
    initializePrequalificationFlow();
  }, []);

  return getPageByStep(currentPreQualificationStep, vfp);
};

export default PreQualification;
